import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-sdk-ng';
import type { RtcToken } from './teleApi';

/**
 * Where the CALL is — kept apart from where the consultation is (the server's
 * session status). CONNECTED means the video SDK confirmed the join; the old
 * page flipped to "connected", and started its call timer, the moment a token
 * came back, with no media anywhere.
 */
export type CallState =
  | 'IDLE'
  | 'REQUESTING_TOKEN'
  | 'CONNECTING'
  | 'CONNECTED'
  | 'RECONNECTING'
  | 'DISCONNECTED'
  | 'ENDED'
  | 'FAILED';

export interface TeleCall {
  state: CallState;
  error: string | null;
  /** The other participant's media, tracked apart from `state`. */
  remotePresent: boolean;
  remoteVideo: boolean;
  micOn: boolean;
  camOn: boolean;
  canSwitchCamera: boolean;
  canChooseSpeaker: boolean;
  join: () => Promise<void>;
  leave: () => Promise<void>;
  toggleMic: () => Promise<void>;
  toggleCam: () => Promise<void>;
  switchCamera: () => Promise<void>;
  nextSpeaker: () => Promise<void>;
  localRef: React.MutableRefObject<HTMLDivElement | null>;
  remoteRef: React.MutableRefObject<HTMLDivElement | null>;
}

/**
 * One teleconsultation call over Agora. Everything it joins with comes from
 * the server: app id, the session's stored channel, token and uid.
 *
 * Cleanup is total — tracks closed, channel left, listeners removed — on
 * unmount, on a route change, and on a tab close/refresh, so a previous
 * consultation's call can never keep the camera or a listener alive.
 */
export function useTeleCall(fetchToken: (() => Promise<RtcToken>) | null): TeleCall {
  const [state, setState] = useState<CallState>('IDLE');
  const [error, setError] = useState<string | null>(null);
  const [remotePresent, setRemotePresent] = useState(false);
  const [remoteVideo, setRemoteVideo] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);

  const client = useRef<IAgoraRTCClient | null>(null);
  const mic = useRef<IMicrophoneAudioTrack | null>(null);
  const cam = useRef<ICameraVideoTrack | null>(null);
  const remoteUser = useRef<IAgoraRTCRemoteUser | null>(null);
  const cameraIdx = useRef(0);
  const speakerIdx = useRef(0);
  const localRef = useRef<HTMLDivElement | null>(null);
  const remoteRef = useRef<HTMLDivElement | null>(null);
  const fetchRef = useRef(fetchToken);
  fetchRef.current = fetchToken;
  const alive = useRef(true);

  const teardown = useCallback(async () => {
    const c = client.current;
    client.current = null;
    mic.current?.close();
    cam.current?.close();
    mic.current = null;
    cam.current = null;
    remoteUser.current = null;
    if (c) {
      c.removeAllListeners();
      try { await c.leave(); } catch { /* already gone */ }
    }
  }, []);

  const join = useCallback(async () => {
    if (client.current || !fetchRef.current) return;
    setError(null);
    setState('REQUESTING_TOKEN');
    let t: RtcToken;
    try {
      t = await fetchRef.current();
    } catch (e) {
      setState('FAILED');
      setError((e as { message?: string })?.message ?? 'The server did not issue a call credential.');
      return;
    }
    setState('CONNECTING');
    try {
      const { default: AgoraRTC } = await import('agora-rtc-sdk-ng');
      AgoraRTC.setLogLevel(3);
      const c = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      client.current = c;

      c.on('user-joined', (u) => { remoteUser.current = u; setRemotePresent(true); });
      c.on('user-left', (u) => {
        if (remoteUser.current?.uid === u.uid) remoteUser.current = null;
        setRemotePresent(false);
        setRemoteVideo(false);
      });
      c.on('user-published', async (u, mediaType) => {
        await c.subscribe(u, mediaType);
        remoteUser.current = u;
        setRemotePresent(true);
        if (mediaType === 'video' && remoteRef.current) {
          u.videoTrack?.play(remoteRef.current);
          setRemoteVideo(true);
        }
        if (mediaType === 'audio') u.audioTrack?.play();
      });
      c.on('user-unpublished', (u, mediaType) => {
        if (mediaType === 'video') setRemoteVideo(false);
        void u;
      });
      c.on('connection-state-change', (cur) => {
        if (!alive.current) return;
        if (cur === 'CONNECTED') setState('CONNECTED');
        else if (cur === 'RECONNECTING') setState('RECONNECTING');
        else if (cur === 'DISCONNECTED') setState((s) => (s === 'ENDED' ? s : 'DISCONNECTED'));
      });
      // A consultation longer than a token renews instead of dropping.
      c.on('token-privilege-will-expire', async () => {
        try { await c.renewToken((await fetchRef.current!()).token); } catch { /* did-expire handles it */ }
      });
      c.on('token-privilege-did-expire', () => {
        setState('FAILED');
        setError('The call credential expired and could not be renewed.');
      });

      await c.join(t.appId, t.channelName, t.token, t.uid);
      const [m, v] = await AgoraRTC.createMicrophoneAndCameraTracks();
      mic.current = m;
      cam.current = v;
      if (localRef.current) v.play(localRef.current);
      await c.publish([m, v]);
      setMicOn(true);
      setCamOn(true);
      const [cams, outs] = await Promise.all([
        AgoraRTC.getCameras().catch(() => []),
        AgoraRTC.getPlaybackDevices().catch(() => []),
      ]);
      setCameras(cams);
      setSpeakers(outs);
      if (alive.current) setState('CONNECTED');
    } catch (e) {
      await teardown();
      const msg = (e as { code?: string; message?: string });
      setState('FAILED');
      setError(
        msg.code === 'PERMISSION_DENIED' || /NotAllowed|Permission/i.test(msg.message ?? '')
          ? 'Camera and microphone access was blocked. Allow them for this site and try again.'
          : msg.code === 'DEVICE_NOT_FOUND' || /NotFound/i.test(msg.message ?? '')
            ? 'No camera or microphone was found on this device.'
            : msg.message ?? 'The call could not open.',
      );
    }
  }, [teardown]);

  const leave = useCallback(async () => {
    await teardown();
    setRemotePresent(false);
    setRemoteVideo(false);
    setState('ENDED');
  }, [teardown]);

  const toggleMic = useCallback(async () => {
    const m = mic.current;
    if (!m) return;
    await m.setMuted(micOn);
    setMicOn(!micOn);
  }, [micOn]);

  const toggleCam = useCallback(async () => {
    const v = cam.current;
    if (!v) return;
    await v.setEnabled(!camOn);
    if (!camOn && localRef.current) v.play(localRef.current);
    setCamOn(!camOn);
  }, [camOn]);

  const switchCamera = useCallback(async () => {
    const v = cam.current;
    if (!v || cameras.length < 2) return;
    cameraIdx.current = (cameraIdx.current + 1) % cameras.length;
    await v.setDevice(cameras[cameraIdx.current]!.deviceId);
  }, [cameras]);

  const nextSpeaker = useCallback(async () => {
    const a = remoteUser.current?.audioTrack;
    if (!a || speakers.length < 2) return;
    speakerIdx.current = (speakerIdx.current + 1) % speakers.length;
    await a.setPlaybackDevice(speakers[speakerIdx.current]!.deviceId);
  }, [speakers]);

  useEffect(() => {
    alive.current = true;
    const onUnload = () => { void teardown(); };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      alive.current = false;
      window.removeEventListener('beforeunload', onUnload);
      void teardown();
    };
  }, [teardown]);

  return {
    state, error, remotePresent, remoteVideo, micOn, camOn,
    canSwitchCamera: cameras.length > 1,
    canChooseSpeaker: speakers.length > 1,
    join, leave, toggleMic, toggleCam, switchCamera, nextSpeaker,
    localRef, remoteRef,
  };
}
